import React, { useEffect, useRef, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import pinPrompt from '../../utils/pinPrompt';
import { tmRequest } from '../../utils/tmApi';
import { reasonOk, TM_REASON_MIN } from '../../utils/tmPricing';

/**
 * Reopen T&M pricing (only while no invoice exists): a reason (>= 5 visible characters), then an
 * Admin / Manager PIN (the normal PIN prompt, auto-fill allowed — decision 13), then
 * POST tm_reopen {pin, reason}. The server restores the lines, header money, tax and quote exactly
 * as they were before the approval.
 *
 * onClose(outcome): { outcome: 'unknown' } when a tm_reopen was sent and did not come back OK (no
 * answer, a gateway error, a 409 because the order already changed ...): the page re-reads the
 * order, since the reopen may have gone through.
 */
export default function TmReopenDialog({ show, salesOrderId, orderNumber, onClose, onReopened }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const unknownRef = useRef(false);

  useEffect(() => {
    if (show) { setReason(''); setError(''); setBusy(false); unknownRef.current = false; }
  }, [show]);

  const close = () => {
    if (busy) return;
    const unknown = unknownRef.current;
    unknownRef.current = false;
    if (!onClose) return;
    if (unknown) onClose({ outcome: 'unknown' });
    else onClose();
  };

  const submit = async () => {
    if (!reasonOk(reason)) return;
    // T&M reopen is PIN-only on the API (_tm_active_user_from_pin never takes a face token).
    const pin = await pinPrompt(`Admin or Manager PIN to reopen T&M pricing on ${orderNumber}:`, { face: false });
    if (!pin) return;
    setBusy(true);
    setError('');
    const r = await tmRequest('POST', `/sales_orders/${salesOrderId}/tm_reopen`, { pin, reason: reason.trim() });
    setBusy(false);
    if (r.ok) {
      unknownRef.current = false;
      if (onReopened) onReopened(r.data);
      return;
    }
    // A PIN / permission refusal changed nothing; anything else may have (or the page is stale).
    if (r.status !== 401 && r.status !== 403) unknownRef.current = true;
    setError(r.text);
  };

  return (
    <Modal show={!!show} onHide={close} centered aria-labelledby="tm-reopen-title">
      <Modal.Header closeButton={!busy}>
        <Modal.Title id="tm-reopen-title" style={{ fontSize: '1.05rem' }}>Reopen T&amp;M pricing — {orderNumber}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="small mb-2">
          The approved prices come off the order and its quote (back to T&amp;M pending, exactly as before the
          approval). Price it again with Approve T&amp;M Pricing.
        </p>
        <label className="form-label small" htmlFor="tm-reopen-reason">Why? (at least {TM_REASON_MIN} characters)</label>
        <textarea
          id="tm-reopen-reason"
          className={`form-control form-control-sm ${reasonOk(reason) ? 'is-valid' : ''}`}
          rows="2"
          autoFocus
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          disabled={busy}
        />
        {error && <div className="alert alert-danger py-2 small mt-2 mb-0" role="alert">{error}</div>}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" size="sm" onClick={close} disabled={busy}>Cancel</Button>
        <Button variant="warning" size="sm" onClick={submit} disabled={busy || !reasonOk(reason)}>
          {busy ? 'Reopening…' : 'Reopen T&M pricing'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
